import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ACTIVETAB, RIGHTSIDECOMPONENTS } from './RightSideComponents';
import { CENTERCOMPONENTS } from './CenterComponents';

interface MapPanelProviderProps {
  children: ReactNode;
}

interface MapPanelContextData {
  activeTab: ACTIVETAB;
  setActiveTab: (activeTab: ACTIVETAB) => void;
  rightSideComponent: RIGHTSIDECOMPONENTS;
  setRightSideComponent: (component: RIGHTSIDECOMPONENTS) => void;
  centerComponent: CENTERCOMPONENTS;
  setCenterComponent: (component: CENTERCOMPONENTS) => void;
  centerComponentData: any;
  setCenterComponentData: (data: any) => void;
  tabChange: boolean;
  setTabChange: (tabChange: boolean) => void;
  tableExpanded: boolean;
  setTableExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  reportDetailsExpanded: boolean;
  setReportDetailsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  resetMapPanelContext: () => void;
  tableHeight: number;
  setTableHeight: React.Dispatch<React.SetStateAction<number>>;
  loadingCenterComponentData: boolean;
  setLoadingCenterComponentData: React.Dispatch<React.SetStateAction<boolean>>;
}

const MapPanelContext = createContext<MapPanelContextData | undefined>(
  undefined
);

export const MapPanelProvider = ({ children }: MapPanelProviderProps) => {
  const [activeTab, setActiveTab] = useState<ACTIVETAB>(ACTIVETAB.NONE);
  const [rightSideComponent, setRightSideComponent] =
    useState<RIGHTSIDECOMPONENTS>(RIGHTSIDECOMPONENTS.NONE);
  const [centerComponent, setCenterComponent] = useState<CENTERCOMPONENTS>(
    CENTERCOMPONENTS.NONE
  );
  const [tableHeight, setTableHeight] = useState<number>(0);
  const [centerComponentData, setCenterComponentData] = useState(null);
  const [loadingCenterComponentData, setLoadingCenterComponentData] =
    useState(false);
  const [tabChange, setTabChange] = useState<boolean>(false);
  const [tableExpanded, setTableExpanded] = useState<boolean>(true);
  const [reportDetailsExpanded, setReportDetailsExpanded] =
    useState<boolean>(true);

  const resetMapPanelContext: () => void = useCallback(() => {
    setActiveTab(ACTIVETAB.NONE);
    setRightSideComponent(RIGHTSIDECOMPONENTS.NONE);
    setCenterComponent(CENTERCOMPONENTS.NONE);
    setCenterComponentData(null);
    setTabChange(false);
    setTableExpanded(true);
    setReportDetailsExpanded(true);
  }, [
    setActiveTab,
    setRightSideComponent,
    setCenterComponent,
    setCenterComponentData,
    setTabChange,
    setTableExpanded,
    setReportDetailsExpanded,
  ]);

  const contextValue = useMemo(
    () => ({
      activeTab,
      setActiveTab,
      rightSideComponent,
      setRightSideComponent,
      centerComponent,
      setCenterComponent,
      centerComponentData,
      setCenterComponentData,
      tabChange,
      setTabChange,
      tableExpanded,
      setTableExpanded,
      reportDetailsExpanded,
      setReportDetailsExpanded,
      resetMapPanelContext,
      tableHeight,
      setTableHeight,
      loadingCenterComponentData,
      setLoadingCenterComponentData,
    }),
    [
      activeTab,
      setActiveTab,
      rightSideComponent,
      setRightSideComponent,
      centerComponent,
      setCenterComponent,
      centerComponentData,
      setCenterComponentData,
      tabChange,
      setTabChange,
      tableExpanded,
      setTableExpanded,
      reportDetailsExpanded,
      setReportDetailsExpanded,
      resetMapPanelContext,
      tableHeight,
      setTableHeight,
      loadingCenterComponentData,
      setLoadingCenterComponentData,
    ]
  );

  return (
    <MapPanelContext.Provider value={contextValue}>
      {children}
    </MapPanelContext.Provider>
  );
};

export const useMapPanelContext = () => {
  const context = useContext(MapPanelContext);
  if (context === undefined) {
    throw new Error(
      'useMapPanelContext must be used within a MapPanelContextProvider'
    );
  }
  return context;
};
